import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
var _excluded = ["className", "style", "renderItem", "width", "bordered", "data", "height", "itemHeight", "itemKey", "draggable", "onDragStart", "onDragEnd"];
import * as React from 'react';
import classNames from 'classnames';
import ListContext from "./context/ListContext";
import VirtualListWrapper from './VirtualListWrapper';
import ListReducer from './context/ListReducer';
import { DragDropContext, Droppable, Draggable, useMouseSensor } from 'react-beautiful-dnd';
import uuid from '../utils/uuidGenerator';
import { getKeyTable } from '../datepicker/utils/NavUtils';
var noop = function noop() {};
var List = /*#__PURE__*/React.forwardRef(function (props, listRef) {
  var _classNames;
  var className = props.className,
    style = props.style,
    renderItem = props.renderItem,
    width = props.width,
    bordered = props.bordered,
    data = props.data,
    height = props.height,
    itemHeight = props.itemHeight,
    itemKey = props.itemKey,
    draggable = props.draggable,
    onDragStart = props.onDragStart,
    onDragEnd = props.onDragEnd,
    restProps = _objectWithoutPropertiesLoose(props, _excluded);
  var prefixCls = "ap-list";
  var classes = classNames(prefixCls, (_classNames = {}, _classNames[prefixCls + "-bordered"] = bordered, _classNames), className);
  var renderList = function renderList(item, index, isDragging) {
    if (renderItem) {
      return renderItem(item, index, isDragging);
    } else {
      return /*#__PURE__*/React.createElement("span", null);
    }
  };
  var _React$useReducer = React.useReducer(ListReducer, {
      data: data,
      draggable: draggable || false,
      isOnKeyboardDragging: false,
      currentDraggingItem: null,
      currentDraggingIndex: null,
      listItemRefList: [],
      keydownHandler: null
    }),
    listStore = _React$useReducer[0],
    listDispatch = _React$useReducer[1];
  React.useEffect(function () {
    listDispatch({
      type: "setDraggable",
      payload: {
        draggable: draggable
      }
    });
  }, [draggable]);
  React.useEffect(function () {
    listDispatch({
      type: "setDynamicData",
      payload: {
        data: data
      }
    });
  }, [data]);
  var initialStore = {
    listStore: listStore,
    listDispatch: listDispatch
  };
  var _onDragEnd = function _onDragEnd(item) {
    if (item.source && item.destination) listDispatch({
      type: "setDataOnDrop",
      payload: {
        draggingIndex: item.source.index,
        targetDropIndex: item.destination.index
      }
    });
    if (onDragEnd) onDragEnd(item);
  };
  var _onDragStart = function _onDragStart(item) {
    if (onDragStart) onDragStart(item);
  };
  var getItemClassName = function getItemClassName(isDragging) {
    return classNames({
      "ap-dragging": isDragging
    }, 'ap-list-item-draggable');
  };
  var handleKeyDown = function handleKeyDown(e, item, index) {
    var _listStore$keydownHan, _listStore$keydownHan2;
    var _getKeyTable = getKeyTable(e),
      isDown = _getKeyTable.isDown,
      isUp = _getKeyTable.isUp,
      isLeft = _getKeyTable.isLeft,
      isRight = _getKeyTable.isRight,
      isEnter = _getKeyTable.isEnter,
      isSpace = _getKeyTable.isSpace;
    if (isSpace || isDown || isUp || isLeft || isRight) {
      e.preventDefault();
    }
    var listItemRefList = listStore.listItemRefList;
    if (isEnter || isSpace) {
      listDispatch({
        type: "triggerIsOnKeyboardDragging",
        payload: {
          currentDraggingIndex: item.id
        }
      });
    }
    if (listStore.draggable && listStore.isOnKeyboardDragging) {
      if (isUp || isLeft) {
        listDispatch({
          type: "setDataOnKeyUp",
          payload: {
            currentItem: item,
            currentIndex: index
          }
        });
        if (index !== undefined) {
          if (listItemRefList && listItemRefList[index] && listItemRefList[index].current) {
            listItemRefList[index].current.focus();
          }
        }
      } else if (isDown || isRight) {
        listDispatch({
          type: "setDataOnKeyDown",
          payload: {
            currentItem: item,
            currentIndex: index
          }
        });
      }
    }
    if (listStore.keydownHandler && listStore.keydownHandler.length) (_listStore$keydownHan = (_listStore$keydownHan2 = listStore.keydownHandler)[index]) === null || _listStore$keydownHan === void 0 ? void 0 : _listStore$keydownHan.call(_listStore$keydownHan2, e, item, index);
  };
  return /*#__PURE__*/React.createElement(ListContext.Provider, {
    value: initialStore
  }, /*#__PURE__*/React.createElement("div", _extends({
    ref: listRef,
    className: classes,
    style: _extends({
      width: width
    }, style)
  }, restProps), draggable ? /*#__PURE__*/React.createElement(DragDropContext, {
    enableDefaultSensors: false,
    sensors: [useMouseSensor],
    onDragEnd: _onDragEnd,
    onDragStart: _onDragStart
  }, /*#__PURE__*/React.createElement(Droppable, {
    droppableId: "ap-list-" + uuid(8, 64)
  }, function (droppableProvided) {
    return /*#__PURE__*/React.createElement("div", {
      ref: droppableProvided.innerRef
    }, listStore.data.map(function (item, index) {
      return /*#__PURE__*/React.createElement(Draggable, {
        key: item.id || String(index),
        draggableId: item.id || String(index),
        index: index
      }, function (draggableProvided, draggableSnapshot) {
        if (draggableProvided.draggableProps) {
          draggableProvided.draggableProps.role = "option";
          draggableProvided.dragHandleProps.role = "option";
        }
        return /*#__PURE__*/React.createElement("div", _extends({
          onKeyDown: function onKeyDown(e) {
            return handleKeyDown(e, item, index);
          },
          ref: draggableProvided.innerRef
        }, draggableProvided.draggableProps, draggableProvided.dragHandleProps, {
          className: getItemClassName(draggableSnapshot.isDragging)
        }), renderList(item, index, draggableSnapshot.isDragging || listStore.isOnKeyboardDragging && item.id === listStore.currentDraggingIndex));
      });
    }), droppableProvided.placeholder);
  })) : /*#__PURE__*/React.createElement(VirtualListWrapper, {
    height: height,
    itemHeight: itemHeight,
    itemKey: itemKey,
    style: {
      boxSizing: 'border-box'
    },
    renderList: renderList
  }, renderList)));
});
export default List;